import { format, parseISO } from "date-fns";
import { cn } from "@/modern/utils";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { CLOCKING_GUIDE_TARGETS, guideTargetProps } from "@/react/guides";
import { buildExceptionInbox } from "../mappers/buildExceptionInbox";
import type { ClockingGridProps } from "../types";
import { DayCellContent } from "./DayCellContent";

const RESOURCE_COL_W = "w-56 min-w-56 max-w-56";
const DAY_COL_W = "w-36 min-w-36";

function formatDayHeader(date: string): { weekday: string; day: string } {
  try {
    const d = parseISO(date);
    return { weekday: format(d, "EEE"), day: format(d, "MMM d") };
  } catch {
    return { weekday: "", day: date };
  }
}

function isSelected(
  selected: ClockingGridProps["selected"],
  cell: { attendanceId: number | null; timeOffId: number | null; date: string },
  resourceId: number,
  groupKey: string
): boolean {
  if (!selected) return false;
  if (selected.attendanceId != null && selected.attendanceId === cell.attendanceId) return true;
  if (
    selected.attendanceId == null &&
    selected.timeOffId != null &&
    selected.timeOffId === cell.timeOffId &&
    selected.resourceId === resourceId &&
    selected.date === cell.date
  ) {
    return true;
  }
  return (
    selected.resourceId === resourceId &&
    selected.date === cell.date &&
    selected.groupKey === groupKey &&
    selected.attendanceId == null &&
    selected.timeOffId == null
  );
}

/**
 * V1 Clocking grid: bordered calendar matrix (resource × date).
 */
export function CustomMatrixClockingGrid({
  model,
  selected,
  onCellSelect,
  onDateHeaderClick,
  isLoading,
}: ClockingGridProps) {
  if (isLoading && !model.groups.length) {
    return (
      <div className="space-y-3 p-5">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (!model.groups.length) {
    return null;
  }

  const openByDate = new Map<string, number>();
  for (const item of buildExceptionInbox(model)) {
    if (!item.actionable) continue;
    openByDate.set(item.date, (openByDate.get(item.date) ?? 0) + 1);
  }

  // Anchor the “clear a day” guide step on one real column header (prefer a day with open work).
  const guideHeaderDate =
    model.dates.find((d) => (openByDate.get(d) ?? 0) > 0) ?? model.dates[0] ?? null;

  return (
    <div className="h-full overflow-auto" {...guideTargetProps(CLOCKING_GUIDE_TARGETS.grid)}>
      <div className="min-w-max p-3 pb-10">
        <div className="rounded-xl border border-border bg-background">
          {/* Date header — sticky vertically; Resource corner also sticky horizontally.
              Top radii match the card so opaque header fills don't square-off the border curve. */}
          <div className="sticky top-0 z-20 flex rounded-t-[calc(0.75rem-1px)] border-b border-border bg-background/95 shadow-[0_4px_10px_-6px_rgba(0,0,0,0.28)] backdrop-blur-sm">
            <div
              className={cn(
                "sticky left-0 z-30 flex items-center rounded-tl-[calc(0.75rem-1px)] border-r border-border bg-background px-5 py-4 text-sm font-semibold text-foreground",
                RESOURCE_COL_W
              )}
            >
              Resource
            </div>
            {model.dates.map((date, dateIndex) => {
              const { weekday, day } = formatDayHeader(date);
              const isLastDate = dateIndex === model.dates.length - 1;
              const openCount = openByDate.get(date) ?? 0;
              const isGuideHeader = date === guideHeaderDate;
              const headerClass = cn(
                "relative flex flex-col items-center justify-center gap-0.5 border-r border-border px-2 py-3.5 last:border-r-0",
                isLastDate && "rounded-tr-[calc(0.75rem-1px)]",
                onDateHeaderClick &&
                  "group/day cursor-pointer transition-colors hover:bg-muted/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                DAY_COL_W
              );
              if (onDateHeaderClick) {
                return (
                  <button
                    key={date}
                    type="button"
                    className={cn(
                      headerClass,
                      openCount > 0 && "bg-primary/[0.04]"
                    )}
                    onClick={() => onDateHeaderClick(date)}
                    title={
                      openCount > 0
                        ? `Clear ${openCount} open exception${openCount === 1 ? "" : "s"} for ${weekday} ${day}`
                        : `Open clear queue for ${weekday} ${day}`
                    }
                    aria-label={
                      openCount > 0
                        ? `Clear ${openCount} exceptions for ${weekday} ${day}`
                        : `Open clear queue for ${weekday} ${day}`
                    }
                    {...(isGuideHeader
                      ? guideTargetProps(CLOCKING_GUIDE_TARGETS.dateHeader)
                      : {})}
                  >
                    <span className="text-sm font-semibold leading-none text-foreground">
                      {weekday}
                    </span>
                    {/* Same slot: date (or “N open”); “Clear” on hover only when there is open work */}
                    <span className="relative h-3 w-full text-center text-xs font-normal leading-none">
                      {openCount > 0 ? (
                        <>
                          <span className="absolute inset-x-0 font-semibold tabular-nums text-primary transition-opacity group-hover/day:opacity-0 group-focus-visible/day:opacity-0">
                            {openCount} open
                          </span>
                          <span className="absolute inset-x-0 font-medium text-primary opacity-0 transition-opacity group-hover/day:opacity-100 group-focus-visible/day:opacity-100">
                            Clear
                          </span>
                        </>
                      ) : (
                        <span className="absolute inset-x-0 text-muted-foreground">
                          {day}
                        </span>
                      )}
                    </span>
                    {openCount > 0 ? (
                      <span
                        className="pointer-events-none absolute inset-x-4 bottom-0 h-[2px] origin-center rounded-full bg-primary/65 transition-all duration-200 group-hover/day:inset-x-2.5 group-hover/day:bg-primary"
                        aria-hidden
                      />
                    ) : null}
                  </button>
                );
              }
              return (
                <div key={date} className={headerClass}>
                  <span className="text-sm font-semibold leading-none text-foreground">
                    {weekday}
                  </span>
                  <span className="text-xs font-normal leading-none text-muted-foreground">
                    {day}
                  </span>
                </div>
              );
            })}
          </div>

          {model.groups.map((group, groupIndex) => (
            <div
              key={group.key}
              className={cn(groupIndex > 0 && "border-t border-border")}
            >
              <div className="flex items-stretch border-b border-border bg-muted/55">
                <div
                  className={cn(
                    "sticky left-0 z-10 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 border-r border-border bg-muted px-4 py-2.5",
                    RESOURCE_COL_W
                  )}
                >
                  <span className="text-sm font-semibold text-foreground">
                    {group.title}
                  </span>
                  {group.subtitle ? (
                    <span className="text-xs text-muted-foreground">{group.subtitle}</span>
                  ) : null}
                  <span className="text-xs text-muted-foreground/80">
                    {group.workerCount} worker{group.workerCount === 1 ? "" : "s"}
                  </span>
                </div>
                {model.dates.map((date) => (
                  <div
                    key={`${group.key}-header-${date}`}
                    className={cn("border-r border-border last:border-r-0", DAY_COL_W)}
                    aria-hidden
                  />
                ))}
              </div>

              {group.rows.map((row) => (
                <div
                  key={`${group.key}-${row.resourceId}`}
                  className="flex items-stretch border-b border-border last:border-b-0 hover:bg-muted/20"
                >
                  <div
                    className={cn(
                      "sticky left-0 z-10 flex flex-col justify-center gap-0.5 border-r border-border bg-background px-4 py-2.5",
                      RESOURCE_COL_W
                    )}
                  >
                    <span className="truncate text-sm font-medium leading-snug text-foreground">
                      {row.name}
                    </span>
                    {row.role ? (
                      <span className="truncate text-xs leading-snug text-muted-foreground">
                        {row.role}
                      </span>
                    ) : null}
                  </div>
                  {row.cells.map((cell) => {
                    const clickable = cell.attendanceId != null || cell.timeOffId != null;
                    const selectedCell = isSelected(selected, cell, row.resourceId, group.key);
                    return (
                      <div
                        key={`${row.resourceId}-${cell.date}`}
                        className={cn(
                          "flex items-center border-r border-border px-2 py-2 last:border-r-0",
                          DAY_COL_W
                        )}
                      >
                        <button
                          type="button"
                          disabled={!clickable}
                          className={cn(
                            "group/cell h-full w-full text-left",
                            clickable ? "cursor-pointer" : "cursor-default"
                          )}
                          onClick={() => {
                            if (clickable) onCellSelect(cell, row, group);
                          }}
                        >
                          <DayCellContent cell={cell} selected={selectedCell} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
